import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';

import { Box, Card } from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { format, parseJSON } from 'date-fns';
import type { MRT_ColumnDef, MRT_PaginationState, MRT_Row, MRT_SortingState } from 'material-react-table';
import { durationOrUnlimited } from 'productConfigurations/durationUnits';

import { DATE_FORMAT } from 'common/constants';
import { usePersistentState } from 'common/hooks';

import { ClaimLicense } from 'commonComponents/ClaimLicense';
import { CellWithEllipsis, mapBoolean, MaterialTable } from 'commonComponents/MaterialTable';
import { PageContainer } from 'commonComponents/PageContainer';
import { SelectProduct } from 'commonComponents/SelectProduct';

import { useUser } from 'api/authorization';
import { LicenseListEntry, loadLicenseList } from 'api/licenses';
import { useCurrentProduct } from 'api/products';
import { setLicenseDetailBackContext } from 'licenses/LicenseDetails/LicenseDetailsContext';
import { expirationDate } from 'licenses/utils';
import { ROUTES } from 'routes/routes';

import { LicenseListActions, LicenseListFilters } from './LicenseListButtons';

type LicenseRow = LicenseListEntry & { error: boolean; strikethrough: boolean };

let columns: Array<MRT_ColumnDef<LicenseRow>> = [
    { header: 'ID', accessorKey: 'id', size: 80, enableGlobalFilter: false },
    { header: 'Configuration', accessorKey: 'configuration', size: 120 },
    { header: 'Internal', accessorFn: license => mapBoolean(license.internal), size: 90, enableGlobalFilter: false },
    { header: 'Serial Number', accessorKey: 'serial_number', size: 100 },
    { header: 'Sales Order Number', accessorKey: 'sales_order_number', size: 100 },
    { header: 'Purchase Order Number', accessorKey: 'purchase_order_number', size: 100 },
    { header: 'Owner ID', accessorKey: 'ownerId', size: 100 },
    { header: 'Customer', accessorKey: 'customerFullName', size: 50 },
    { header: 'Company', accessorKey: 'customerCompany', size: 130 },
    {
        header: 'Creation',
        accessorFn: license => license.creation_date && format(parseJSON(license.creation_date), DATE_FORMAT),
        size: 100,
    },
    {
        header: 'Validity',
        accessorFn: license => durationOrUnlimited(license.validity, license.validityUnit),
        size: 90,
        enableGlobalFilter: false,
    },
    {
        header: 'Expiration Date',
        accessorFn: license => expirationDate(license, license.expiration_date),
        size: 120,
    },
    {
        header: 'Support Expiration',
        accessorFn: license => expirationDate(license, license.support_expiration_date),
        size: 130,
    },
    {
        header: 'Renewed',
        accessorFn: license => license.renewal_date && format(parseJSON(license.renewal_date), DATE_FORMAT),
        size: 100,
        enableGlobalFilter: false,
    },
    { header: 'Creator', accessorKey: 'creator', size: 100 },
    { header: '# of Files', accessorKey: 'licenseFilesCount', size: 90 },
    {
        header: 'System ID',
        accessorKey: 'latestLicenseFileHardwareIdentifiers',
        Cell: CellWithEllipsis,
        size: 130,
    },
];

// Separate statement so type inference can do its magic
columns = columns.map(c => ({ ...c, id: c.header }));

const defaultSort = [{ id: 'Creation', desc: true }];

type ReducerState = {
    params: {
        filter: string;
        isExpired?: boolean;
        isInternal?: boolean;
        allLicenseFilesExpired?: boolean;
        invalidated?: boolean;
        isMaintenanceExpired?: boolean;
        page: number;
        page_size: number;
        product?: number;
        order?: string;
    };
    pagination: MRT_PaginationState;
};
type ReducerActions =
    | 'resetPagination'
    | 'setCurrentProduct'
    | 'setFilter'
    | 'setPagination'
    | 'setSorting'
    | 'setHideExpiredLicenses'
    | 'setHideInternalLicenses'
    | 'setHideInvalidLicenses'
    | 'setHideLicensesWithExpiredMaintenance';

function setPaginationState(state: ReducerState, pagination: MRT_PaginationState): ReducerState {
    return {
        params: {
            ...state.params,
            page: pagination.pageIndex + 1,
            page_size: pagination.pageSize,
        },
        pagination,
    };
}

export function LicenseList() {
    const { data: currentProduct } = useCurrentProduct();
    const { data: user } = useUser();
    const userHasProduct = Boolean(user?.products?.length);
    const productIsSelected = Boolean(currentProduct);
    const navigate = useNavigate();
    const location = useLocation();

    const defaultAdditionalFilters = useMemo(
        () => ({
            hideExpired: true,
            hideInternal: true,
            hideInvalid: true,
            hideWithExpiredMaintenance: false,
        }),
        []
    );
    const [additionalFilters, persistAdditionalFilters] = usePersistentState(
        'licenseAdditionalFilters',
        defaultAdditionalFilters
    );
    const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 0, pageSize: 100 });

    const resetPagination = useCallback((state: ReducerState): ReducerState => {
        setPagination(pagination => ({ pageIndex: 0, pageSize: pagination.pageSize }));
        return setPaginationState(state, { pageIndex: 0, pageSize: state.pagination.pageSize });
    }, []);

    const [state, dispatch] = useReducer(
        function reducer(state: ReducerState, action: { type: ReducerActions; payload: any }): ReducerState {
            if (action.type === 'resetPagination') {
                return resetPagination(state);
            }
            if (action.type === 'setPagination') {
                setPagination(action.payload);
                return setPaginationState(state, action.payload);
            } else if (action.type === 'setCurrentProduct') {
                return resetPagination({
                    params: {
                        ...state.params,
                        product: action.payload,
                    },
                    pagination: {
                        ...state.pagination,
                    },
                });
            } else if (action.type === 'setHideExpiredLicenses') {
                persistAdditionalFilters(additionalFilters => ({ ...additionalFilters, hideExpired: action.payload }));
                return resetPagination({
                    params: {
                        ...state.params,
                        isExpired: !action.payload ? undefined : false,
                        allLicenseFilesExpired: !action.payload ? undefined : false,
                    },
                    pagination: state.pagination,
                });
            } else if (action.type === 'setHideInternalLicenses') {
                persistAdditionalFilters(additionalFilters => ({ ...additionalFilters, hideInternal: action.payload }));
                return resetPagination({
                    params: {
                        ...state.params,
                        isInternal: !action.payload ? undefined : false,
                    },
                    pagination: state.pagination,
                });
            } else if (action.type === 'setHideInvalidLicenses') {
                persistAdditionalFilters(additionalFilters => ({ ...additionalFilters, hideInvalid: action.payload }));
                return resetPagination({
                    params: {
                        ...state.params,
                        invalidated: !action.payload ? undefined : false,
                    },
                    pagination: state.pagination,
                });
            } else if (action.type === 'setHideLicensesWithExpiredMaintenance') {
                persistAdditionalFilters(additionalFilters => ({
                    ...additionalFilters,
                    hideWithExpiredMaintenance: action.payload,
                }));
                return resetPagination({
                    params: {
                        ...state.params,
                        isMaintenanceExpired: !action.payload ? undefined : false,
                    },
                    pagination: state.pagination,
                });
            } else if (action.type === 'setFilter') {
                return resetPagination({
                    params: { ...state.params, filter: action.payload },
                    pagination: state.pagination,
                });
            } else if (action.type === 'setSorting') {
                return {
                    params: {
                        ...state.params,
                        order: !action.payload.length
                            ? null
                            : action.payload
                                  .map((s: { id: string; desc: boolean }) => (s.desc ? `-${s.id}` : s.id))
                                  .join(','),
                    },
                    pagination: state.pagination,
                };
            }
            return state;
        },
        {
            params: {
                filter: '',
                isExpired: !additionalFilters.hideExpired ? undefined : false,
                allLicenseFilesExpired: !additionalFilters.hideExpired ? undefined : false,
                isInternal: !additionalFilters.hideInternal ? undefined : false,
                invalidated: !additionalFilters.hideInvalid ? undefined : false,
                isMaintenanceExpired: !additionalFilters.hideWithExpiredMaintenance ? undefined : false,
                page: 1,
                page_size: 15,
                product: currentProduct?.id,
                order: undefined,
            },
            pagination: {
                pageIndex: 0,
                pageSize: 100,
            },
        }
    );

    const filterFromUrl = useMemo(() => {
        const params = new URLSearchParams(location.search);
        const raw = params.get('filter');
        return raw ? decodeURIComponent(raw) : '';
    }, [location.search]);

    const initialFilter = filterFromUrl;

    useEffect(() => {
        if (!filterFromUrl) return;
        dispatch({ type: 'setFilter', payload: filterFromUrl });
        // Remove the query parameter from URL to avoid re-applying on refresh
        navigate(location.pathname, { replace: true });
    }, [filterFromUrl, dispatch, navigate, location.pathname]);

    const Buttons = (
        <LicenseListFilters
            hideExpiredLicenses={additionalFilters.hideExpired}
            setHideExpiredLicenses={(flag: boolean) => {
                dispatch({ type: 'setHideExpiredLicenses', payload: flag });
            }}
            hideInternalLicenses={additionalFilters.hideInternal}
            setHideInternalLicenses={(flag: boolean) => {
                dispatch({ type: 'setHideInternalLicenses', payload: flag });
            }}
            hideInvalidLicenses={additionalFilters.hideInvalid}
            setHideInvalidLicenses={(flag: boolean) => {
                dispatch({ type: 'setHideInvalidLicenses', payload: flag });
            }}
            hideLicensesWithExpiredMaintenance={additionalFilters.hideWithExpiredMaintenance}
            setHideLicensesWithExpiredMaintenance={(flag: boolean) => {
                dispatch({ type: 'setHideLicensesWithExpiredMaintenance', payload: flag });
            }}
        />
    );

    useEffect(() => {
        dispatch({ type: 'setCurrentProduct', payload: currentProduct?.id });
    }, [currentProduct]);

    const { data, isFetched, isLoading } = useQuery({
        placeholderData: keepPreviousData,
        queryKey: ['License', 'list', state.params],
        queryFn: () => loadLicenseList(state.params),
        select: data => {
            return {
                ...data,
                results: data.results.map(license => ({
                    ...license,

                    error: license.isExpired || license.allLicenseFilesExpired,
                    strikethrough: license.invalidated,
                })),
            };
        },
        enabled: state.params.order !== undefined,
        refetchOnWindowFocus: false,
    });
    const licenses: Array<LicenseRow> = useMemo(() => data?.results || [], [data]);
    const rowCount = data ? data.count : 0;

    const showDetails = (row: MRT_Row<LicenseRow>) =>
        navigate(generatePath(ROUTES.LICENSE_DETAIL, { id: row.original.id.toString() }));

    const onSortingChanged = useCallback(
        (sorting: MRT_SortingState) => dispatch({ type: 'setSorting', payload: sorting }),
        []
    );
    const onFilterChanged = useCallback((filter: any) => dispatch({ type: 'setFilter', payload: filter }), []);
    useEffect(() => dispatch({ type: 'setPagination', payload: pagination }), [pagination]);

    useEffect(() => {
        setLicenseDetailBackContext([{ url: ROUTES.LICENSE_LIST, label: 'Licenses' }]);
    }, []);

    return (
        <PageContainer dataTestId="LicenseList_Container" maxWidth={false} maxHeight="100%">
            {!userHasProduct && <ClaimLicense />}
            {userHasProduct && !productIsSelected && <SelectProduct />}
            {userHasProduct && productIsSelected && (
                <>
                    <Card sx={{ padding: 1, marginBottom: 1, overflow: 'inherit' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <LicenseListActions />
                        </Box>
                    </Card>

                    <MaterialTable
                        name="licenses"
                        data={licenses}
                        columns={columns}
                        defaultSort={defaultSort}
                        rowCount={rowCount}
                        onRowClicked={showDetails}
                        customActions={Buttons}
                        isInitialLoading={isLoading}
                        showProgressBars={!isFetched}
                        isServerFiltered
                        onFilterChanged={onFilterChanged}
                        pagination={pagination}
                        setPagination={setPagination}
                        onSortingChanged={onSortingChanged}
                        initialFilter={initialFilter}
                    />
                </>
            )}
        </PageContainer>
    );
}
